import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import InputField from "../../../components/InputField";
import Button from "../../../components/Button";
import Breadcrumbs from "../../../components/Breadcrumbs";
import api from "../../../api/api";
import SaveIcon from "@mui/icons-material/Save";
import Select from "react-select";
import InfoIcon from "@mui/icons-material/Info";
import Tooltip from "@mui/material/Tooltip";
import "../../form.css";
import SelectField from "../../../components/SelectField";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

const InvoicingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
 
  // Form state
   const [formData, setFormData] = useState({
     job_number: "",
     lut_number: "",
     inv_number: "",
     bl_number: "",
     bl_source: "",
     other_reference: "",
     invoice_date: "",
     billing_party: "",
     shipper: "",
     consignee: "",
     notify_party: "",
     pol: "",
     pod: "",
     place_of_receipt: "",
     final_delivery: "",
     shipment_segment: "",
     incoterms: "",
     commodity_name: "",
     freight_terms: "",
     gross_weight: "",
     cbm: "",
     chargeable_weight: "",
     container_number: "",
     airline_name: "",
     consolidation_at: "",
     vessel_name: "",
     voyage_number: "",
     term_and_condition: "",
   });
 
   const [portsOptions, setPortsOptions] = useState([]);
   const [loading, setLoading] = useState(false);
   const [loadingData, setLoadingData] = useState(true);
   const [loadingPorts, setLoadingPorts] = useState(true);
   const [chargeCodeOptions, setChargeCodeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [loadingChargeCodes, setLoadingChargeCodes] = useState(false);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [gstPercentageCache, setGstPercentageCache] = useState({});
  const [sacCodeCache, setSacCodeCache] = useState({});
  const [loadingGstForCharge, setLoadingGstForCharge] = useState(false);
   const [containerTableData, setContainerTableData] = useState([]);
   const [inputChargeRow, setInputChargeRow] = useState({
     charge_code: "",
     sac_code: "",
     default_gst_percentage: 0,
     currency: "",
     exchange_rate: 1,
     rate: 0,
     quantity: 0,
     amount: 0,
     amount_in_inr: 0,
     gst_option: null,
     gst_percentage: 0,
     gst_amount: 0,
   });
    const [chargeRow, setChargeRow] = useState([]);

    const [totals, setTotals] = useState({
      subTotal: 0,
      gstAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      grandTotal: 0,
    });

    // Auto-recalculate totals whenever chargeRow changes (add/delete)
    useEffect(() => {
      const subTotal = chargeRow.reduce((acc, row) => acc + Number(row.amount_in_inr || 0), 0);
      const gstAmount = chargeRow.reduce((acc, row) => {
        return acc + (row.gst_option?.value === "yes" ? Number(row.gst_amount || 0) : 0);
      }, 0);

      setTotals({
        subTotal: Number(subTotal.toFixed(2)),
        gstAmount: Number(gstAmount.toFixed(2)),
        cgstAmount: Number((gstAmount / 2).toFixed(2)),
        sgstAmount: Number((gstAmount / 2).toFixed(2)),
        grandTotal: Number((subTotal + gstAmount).toFixed(2)),
      });
    }, [chargeRow]);

    const [errors, setErrors] = useState({
      form: {},
      containerTable: "",
      chargeTable: "",
    });
    const [showConfirm, setShowConfirm] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [error, setError] = useState(null);
    const [invoiceType, setInvoiceType] = useState(null); // "performa" or "final"

    const [dateOfIssue, setDateOfIssue] = useState(null);
    const [dateOfIssueError, setDateOfIssueError] = useState("");

   const handleContainerChange = (index, field, value) => {
     setContainerTableData((prev) => {
       const updated = [...prev];
       updated[index] = { ...updated[index], [field]: value };
       return updated;
     });
   };
 
  useEffect(() => {
    const loadData = async () => {
      await fetchPorts();
      if (id && id !== "new") {
        await fetchEnquiryData(id);
      } else {
        setLoadingData(false);
      }
    };
    loadData();
  }, [id]);
 
   const fetchGSTForChargeCode = async (charge_code, index) => {
    if (!charge_code) return;

    try {
      setLoadingGstForCharge(true);
      const response = await api.get(`/charge_code/gst/${charge_code}`);
      const gstPercentage = response.data.tax_percentage || 0;
      const sacCode = response.data.sac_code || "";

      // Cache the result
      setGstPercentageCache((prev) => ({ ...prev, [charge_code]: gstPercentage }));
      setSacCodeCache((prev) => ({ ...prev, [charge_code]: sacCode }));

      if (index === "input") {
        setInputChargeRow((prev) => {
          // Only apply if this charge code is still currently selected
          if (prev.charge_code !== charge_code) {
            return prev;
          }
          const isGstEnabled = prev.gst_option?.value === "yes";
          const activeGstRate = isGstEnabled ? gstPercentage : 0;

          const rate = parseFloat(prev.rate) || 0;
          const qty = parseFloat(prev.quantity) || 0;
          const exchange = parseFloat(prev.exchange_rate) || 1;

          const amount = rate * qty;
          const amountInInr = prev.currency === "INR" ? amount : amount * exchange;
          const gstAmount = isGstEnabled ? (amountInInr * activeGstRate) / 100 : 0;

          return {
            ...prev,
            sac_code: sacCode,
            default_gst_percentage: gstPercentage,
            gst_percentage: activeGstRate,
            amount: Number(amount.toFixed(2)),
            amount_in_inr: Number(amountInInr.toFixed(2)),
            gst_amount: Number(gstAmount.toFixed(2)),
          };
        });
      }

      // Also update any rows in chargeRow that have this charge_code and GST enabled
      setChargeRow((prev) =>
        prev.map((row) => {
          if (row.charge_code === charge_code && row.gst_option?.value === "yes") {
            const activeGstRate = gstPercentage;
            const rate = parseFloat(row.rate) || 0;
            const qty = parseFloat(row.quantity) || 0;
            const exchange = parseFloat(row.exchange_rate) || 1;
            const amount = rate * qty;
            const amountInInr = row.currency === "INR" ? amount : amount * exchange;
            const gstAmount = (amountInInr * activeGstRate) / 100;

            return {
              ...row,
              sac_code: sacCode,
              default_gst_percentage: gstPercentage,
              gst_percentage: activeGstRate,
              gst_amount: Number(gstAmount.toFixed(2)),
            };
          }
          return row;
        })
      );
    } catch (err) {
      console.error("Failed to fetch GST for charge code:", err);
    } finally {
      setLoadingGstForCharge(false);
    }
  };
  const handleCurrencyChange = async (index, selectedCurrency) => {
    const country_code = selectedCurrency?.value || "";

    if (index === "input") {
      handleInputChargeChange("currency", country_code);
    } else {
      handleChargeInputChange(index, "currency", country_code);
    }

    if (!country_code) {
      if (index === "input") {
        handleInputChargeChange("exchange_rate", "");
      } else {
        handleChargeInputChange(index, "exchange_rate", "");
      }
      return;
    }

    // INR is base currency - no fetch needed
    if (country_code === "INR") {
      if (index === "input") {
        handleInputChargeChange("exchange_rate", 1);
      } else {
        handleChargeInputChange(index, "exchange_rate", 1);
      }
      return;
    }

    try {
      const res = await api.get(`/exchange_rate/rate/${country_code}`);
      const exchangeRateFromApi = res.data?.rate_in_NRO || res.data?.rate_in_INR || 1;
      const exchangeRate = Number(exchangeRateFromApi);

      if (index === "input") {
        handleInputChargeChange("exchange_rate", exchangeRate);
      } else {
        handleChargeInputChange(index, "exchange_rate", exchangeRate);
      }
    } catch (err) {
      console.error("Failed to fetch exchange rate:", err);
      if (index === "input") {
        handleInputChargeChange("exchange_rate", "N/A");
      } else {
        handleChargeInputChange(index, "exchange_rate", "N/A");
      }
    }
  };

    const handleInputChargeChange = (field, value) => {
      setInputChargeRow((prev) => {
        const updated = { ...prev, [field]: value };

        // If charge code changes, reset GST-related fields then fetch/restore GST
        if (field === "charge_code") {
          updated.sac_code = "";
          updated.gst_percentage = 0;
          updated.gst_amount = 0;
          
          // Try to get GST from cache for the new charge code
          const cachedGst = gstPercentageCache[value];
          updated.default_gst_percentage = cachedGst !== undefined ? cachedGst : 0;
          
          // If GST was already set to "yes" before charge code change, apply the cached GST immediately
          if (prev.gst_option?.value === "yes") {
            updated.gst_percentage = cachedGst !== undefined ? cachedGst : 0;
          }
          
          // Trigger fetch if not already cached
          if (cachedGst === undefined) {
            fetchGSTForChargeCode(value, "input");
          }
        }

        // If GST option toggled, set GST percentage accordingly
        if (field === "gst_option") {
          if (value?.value === "yes") {
            // Check cache first, then default_gst_percentage
            const cachedGst = gstPercentageCache[updated.charge_code];
            const gstPct = cachedGst !== undefined ? cachedGst : (updated.default_gst_percentage || 0);
            updated.gst_percentage = gstPct;
          } else {
            updated.gst_percentage = 0;
          }
        }

        const rate = parseFloat(updated.rate) || 0;
        const qty = parseFloat(updated.quantity) || 0;
        const exchange = parseFloat(updated.exchange_rate) || 1;

        const amount = rate * qty;
        const isGstEnabled = updated.gst_option?.value === "yes";
        // gstPercentage is already set correctly from cache/default above
        const gstPercentage = isGstEnabled ? parseFloat(updated.gst_percentage) || 0 : 0;

        let amountInInr;
        if (!updated.currency) {
          amountInInr = amount;
        } else if (updated.currency === "INR") {
          amountInInr = amount;
        } else {
          amountInInr = amount * exchange;
        }

        const gstAmount = isGstEnabled ? (amountInInr * gstPercentage) / 100 : 0;

        updated.amount = Number(amount.toFixed(2));
        updated.amount_in_inr = Number(amountInInr.toFixed(2));
        updated.gst_amount = Number(gstAmount.toFixed(2));

        return updated;
      });
    };

    const addChargeRow = () => {
      if (
        !inputChargeRow.charge_code ||
        !inputChargeRow.currency ||
        !inputChargeRow.rate ||
        !inputChargeRow.quantity
      ) {
        setErrors((prev) => ({
          ...prev,
          chargeTable:
            "Please fill all required fields in the input row before adding a new row",
        }));
        return;
      }

      setErrors((prev) => ({ ...prev, chargeTable: "" }));

      // Determine GST percentage: use cached value if available, otherwise use current gst_percentage
      const finalGstPercentage = inputChargeRow.gst_option?.value === "yes"
        ? (gstPercentageCache[inputChargeRow.charge_code] !== undefined
            ? gstPercentageCache[inputChargeRow.charge_code]
            : inputChargeRow.gst_percentage || 0)
        : 0;

      const newCharge = {
        ...inputChargeRow,
        gst_percentage: finalGstPercentage,
      };

      // Recalculate GST amount if GST enabled
      if (inputChargeRow.gst_option?.value === "yes" && finalGstPercentage > 0) {
        newCharge.gst_amount = Number((newCharge.amount_in_inr * finalGstPercentage / 100).toFixed(2));
      }

      setChargeRow((prev) => [...prev, newCharge]);

      // Reset inputChargeRow but preserve exchange rate context and default GST
      setInputChargeRow({
        charge_code: "",
        sac_code: "",
        default_gst_percentage: inputChargeRow.default_gst_percentage,
        currency: "",
        exchange_rate: 1,
        rate: 0,
        quantity: 0,
        amount: 0,
        amount_in_inr: 0,
        gst_option: null,
        gst_percentage: inputChargeRow.default_gst_percentage || 0,
        gst_amount: 0,
      });
    };

    const handleChargeInputChange = (index, field, value) => {
      setChargeRow((prev) => {
        const updated = [...prev];
        updated[index][field] = value;

        // If GST option toggled on existing row, set GST percentage accordingly
        if (field === "gst_option") {
          if (value?.value === "yes") {
            const chargeCode = updated[index].charge_code;
            const cachedGst = gstPercentageCache[chargeCode];
            updated[index].gst_percentage = cachedGst !== undefined ? cachedGst : (updated[index].default_gst_percentage || 0);
          } else {
            updated[index].gst_percentage = 0;
          }
        }

        const rate = parseFloat(updated[index].rate) || 0;
        const qty = parseFloat(updated[index].quantity) || 0;
        const exchange = parseFloat(updated[index].exchange_rate) || 1;
        const isGstEnabled = updated[index].gst_option?.value === "yes";
        let gstPercentage = isGstEnabled ? parseFloat(updated[index].gst_percentage) || 0 : 0;

        // If GST enabled but percentage is 0, try cache
        if (isGstEnabled && gstPercentage === 0) {
          const chargeCode = updated[index].charge_code;
          const cachedGst = gstPercentageCache[chargeCode];
          if (cachedGst !== undefined) {
            gstPercentage = cachedGst;
            updated[index].gst_percentage = gstPercentage;
          }
        }

        const amount = rate * qty;
        const amountInInr = updated[index].currency === "INR" ? amount : amount * exchange;
        const gstAmount = isGstEnabled ? (amountInInr * gstPercentage) / 100 : 0;

        updated[index].amount = Number(amount.toFixed(2));
        updated[index].amount_in_inr = Number(amountInInr.toFixed(2));
        updated[index].gst_amount = Number(gstAmount.toFixed(2));

        return updated;
      });
    };

    const deleteChargeRow = (index) => {
      setChargeRow((prev) => {
        const updatedCharges = prev.filter((_, i) => i !== index);
        return updatedCharges;
      });
    };
  
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };
 
  const fetchPorts = async () => {
    try {
      setLoadingPorts(true);
      const res = await api("/ports");
      const options = res.data.map((p) => ({
        value: p.Port_id,
        label: p.Port_names,
      }));
      setPortsOptions(options);
    } catch (err) {
      console.error("Error fetching ports:", err);
    } finally {
      setLoadingPorts(false);
    }
  };
 
  const fetchEnquiryData = async (enqId) => {
    try {
      setLoadingData(true);
      const res = await api(`/enquiry/viewEnquiry/${enqId}`);
      const data = res.data;
      const enquiry = data;
      if (enquiry) {
        const isFCL = ["FE", "FI"].includes(enquiry.shipment_segment);
        const isLCL = ["LE", "LI"].includes(enquiry.shipment_segment);
        const isAir = ["AE", "AI"].includes(enquiry.shipment_segment);
        const isExport = ["FE", "LE", "AE"].includes(enquiry.shipment_segment);

        setFormData((prev) => ({
          ...prev,
          job_number: enquiry.job_number || "",
          billing_party: "",
          shipper: enquiry.first_shipper_name || "",
          consignee: enquiry.first_consignee_name || "",
          notify_party: enquiry.notify_party || "",
          pol: enquiry.pol || "",
          pod: enquiry.pod || "",
          place_of_receipt: enquiry.place_of_receipt || "",
          final_delivery: enquiry.final_delivery || "",
          shipment_segment: enquiry.shipment_segment || "",
          incoterms: enquiry.incoterm || "",
          commodity_name: enquiry.hs_description || "",
          freight_terms: enquiry.freight_terms || "",
          gross_weight: enquiry.gross_weight || "",
          cbm: enquiry.cbm || "",
          chargeable_weight: enquiry.chargeable_weight || "",
          container_number: isFCL ? (enquiry.container_number || "") : "",
          airline_name: isAir ? (enquiry.airline_name || "") : "",
          consolidation_at: isLCL ? (enquiry.stuffing_destuffing_At || "") : "",
          vessel_name: enquiry.vessel_name || "",
          voyage_number: enquiry.voyage_number || "",
          bl_number: enquiry.bl_number || "",
          other_reference: enquiry.other_reference || "",
        }));
        setContainerTableData(
          res.data.containerDetails?.map((c) => ({
            container_type: c.container_type,
            container_number: c.container_number || "",
            seal_number: c.seal_number || "",
            container_weight: c.container_weight,
            container_length: c.container_length,
            container_breadth: c.container_breadth,
            container_height: c.container_height,
            container_temperature: c.container_temperature,
          })) || []
        );
      }

      // Fetch quotation charges for this enquiry
      try {
        const qRes = await api.get(`/quotation/viewQuotation/${enqId}`);
        const qData = qRes.data;
        const qCharges = qData?.quotation_charges || [];
        if (qCharges.length > 0) {
          const mapped = qCharges.map((qc) => ({
            charge_code: qc.charge_code || "",
            sac_code: qc.sac_code || "",
            currency: qc.charge_currency || "",
            exchange_rate: qc.exchange_rate || 1,
            rate: qc.rate || "",
            quantity: qc.quantity || 0,
            amount: qc.amount || 0,
            amount_in_inr: qc.amount_in_inr || qc.amount || 0,
            gst_option:
              qc.gst_percentage && qc.gst_percentage > 0
                ? { value: "yes", label: "Yes" }
                : { value: "no", label: "No" },
            gst_percentage: qc.gst_percentage || 0,
            gst_amount: qc.gst_amount || 0,
          }));
          setChargeRow(mapped);
        }
      } catch (qErr) {
        console.error("Failed to fetch quotation charges:", qErr);
      }
    } catch (error) {
      console.error("❌ Error fetching enquiry data:", error);
    } finally {
      setLoadingData(false);
    }
  };
  useEffect(() => {
    const fetchChargeCodes = async () => {
      try {
        setLoadingChargeCodes(true);
        const response = await api.get("/charge_code/options");
        // console.log("response.data", response.data);
 
        setChargeCodeOptions(response.data);
      } catch (err) {
        console.error("Failed to fetch charge codes:", err);
      } finally {
        setLoadingChargeCodes(false);
      }
    };
 
    fetchChargeCodes();
  }, []);
 
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        setLoadingCurrencies(true);
        const response = await api.get("/exchange_rate");

        const formatted = response.data.map((item) => ({
          value: item.country_code,
          label: item.country_code,
        }));

        setCurrencyOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch exchange rates:", err);
      } finally {
        setLoadingCurrencies(false);
      }
    };

    fetchExchangeRates();
  }, []);
 
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
 
  const handlePolChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      pol: opt?.value || "",
    }));
  };
 
  const handlePodChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      pod: opt?.value || "",
    }));
  };
 
   const validate = () => {
     const err = {};

     // Required fields
     if (!formData.job_number) err.job_number = "Job Number is required";
     if (!formData.inv_number) err.inv_number = "Invoice Number is required";
     if (!formData.bl_number) err.bl_number = "BL Number is required";
     if (!formData.invoice_date) err.invoice_date = "Invoice Date is required";
     if (!formData.billing_party) err.billing_party = "Billing Party is required";
     if (!formData.shipper) err.shipper = "Shipper is required";
     if (!formData.consignee) err.consignee = "Consignee is required";
     if (!formData.pol) err.pol = "Port of Loading is required";
     if (!formData.pod) err.pod = "Port of Discharge is required";
     if (!formData.shipment_segment) err.shipment_segment = "Shipment Segment is required";
     if (!formData.commodity_name) err.commodity_name = "Commodity Name is required";
     if (!formData.freight_terms) err.freight_terms = "Freight Terms is required";
     if (!formData.vessel_name) err.vessel_name = "Vessel Name is required";
     if (!formData.term_and_condition) err.term_and_condition = "Term & Conditions is required";

     // Conditional validations
     if (["AE", "AI"].includes(formData.shipment_segment) && !formData.airline_name) {
       err.airline_name = "Airline Name is required for air shipments";
     }
      if (["LE", "LI"].includes(formData.shipment_segment) && !formData.consolidation_at) {
        err.consolidation_at = formData.shipment_segment === "LI"
          ? "Destuffing At is required for LCL import shipments"
          : "Stuffing At is required for LCL export shipments";
      }

      // Charges validation
     if (chargeRow.length === 0) {
       err.chargeTable = "At least one charge is required";
     }

      setErrors(err);
      return Object.keys(err).length === 0;
    };

    const submitInvoice = async () => {
      setLoadingSubmit(true);
      try {
        const data = {
          enq_id: id,
          job_number: formData.job_number,
          lut_number: formData.lut_number,
          inv_number: formData.inv_number,
          bl_number: formData.bl_number,
          other_reference: formData.other_reference,
          invoice_date: formData.invoice_date,
          billing_party: formData.billing_party,
          shipper: formData.shipper,
          consignee: formData.consignee,
          notify_party: formData.notify_party,
          pol: formData.pol,
          pod: formData.pod,
          place_of_receipt: formData.place_of_receipt,
          final_delivery: formData.final_delivery,
          shipment_segment: formData.shipment_segment,
          incoterms: formData.incoterms,
          commodity_name: formData.commodity_name,
          freight_terms: formData.freight_terms,
          gross_weight: formData.gross_weight,
          cbm: formData.cbm,
          chargeable_weight: formData.chargeable_weight,
          containers: containerTableData,
          airline_name: formData.airline_name,
          consolidation_at: formData.consolidation_at,
          vessel_name: formData.vessel_name,
          voyage_number: formData.voyage_number,
          term_and_condition: formData.term_and_condition,
          sub_total: totals.subTotal,
          gst_total: totals.gstAmount,
          grand_total: totals.grandTotal,
          invoice_type: invoiceType, // "performa" or "final"
          charges: chargeRow.map((c) => ({
            charge_code: c.charge_code,
            sac_code: c.sac_code,
            currency: c.currency,
            exchange_rate: c.exchange_rate,
            rate: c.rate,
            quantity: c.quantity,
            amount: c.amount,
            amount_in_inr: c.amount_in_inr,
            gst_option: c.gst_option?.value || "no",
            gst_percentage: c.gst_percentage,
            gst_amount: c.gst_amount,
          })),
        };

        // Use different endpoints based on invoice type
        const endpoint = invoiceType === "performa"
          ? "/invoices/createPerforma"
          : "/invoices/create";

        const res = await api.post(endpoint, data);
        if (res.data.success) {
          setShowConfirm(false);
          setShowSuccess(true);
        }
      } catch (err) {
        console.error("Failed to create invoice:", err);
        setError("Failed to create invoice. Please try again.");
      } finally {
        setLoadingSubmit(false);
      }
    };

    const handleSubmit = (e, type = "final") => {
      e.preventDefault();

      if (!validate()) return;
      setInvoiceType(type);
      setShowConfirm(true);
    };

    if (loadingData || loadingPorts) {
      return (
        <>
          <Breadcrumbs />
          <div className="text-center py-5">Loading...</div>
        </>
      );
    }

    return (
    <>
       <Breadcrumbs />

       <form onSubmit={handleSubmit}>
        {/* <h5 className="mb-3 border-bottom pb-2">As Per Our Details</h5> */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <InputField
              label="Job Number"
              name="job_number"
              value={formData.job_number}
              onChange={handleChange}
              disabled
            />
          </div>
          {/* <div className="col-md-4">
            <InputField
              label="LUT Number"
              name="lut_number"
              value={formData.lut_number}
              onChange={handleChange}
              placeholder="Enter LUT Number"
            />
          </div> */}
          <div className="col-md-4">
            <InputField
              label="Invoice Number"
              name="inv_number"
              value={formData.inv_number}
              onChange={handleChange}
              placeholder="Enter Invoice Number"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label={formData.shipment_segment === "AE" || formData.shipment_segment === "AI" ? "AWB Number" : (formData.bl_number && formData.bl_source !== "NaavVaayu" ? "MBL Number" : "HBL Number")}
              name="bl_number"
              value={formData.bl_number}
              onChange={handleChange}
              placeholder="Enter BL Number"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Other Reference (Commercial Inv No)"
              name="other_reference"
              value={formData.other_reference}
              onChange={handleChange}
              placeholder="Enter Other Reference / Commercial Invoice Number"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Invoice Date</label>
            <div style={{ position: "relative", width: "100%" }}>
              <DatePicker
                selected={dateOfIssue}
                onChange={(date) => {
                  setDateOfIssue(date);
                  handleFormChange("invoice_date", date);
                  if (date) setDateOfIssueError("");
                }}
                dateFormat="dd/MM/yyyy"
                wrapperClassName="w-100"
                className={`form-control w-100 ${dateOfIssueError ? "is-invalid" : ""}`}
                placeholderText="DD/MM/YYYY"
                calendarClassName="custom-calendar"
              />
              <CalendarMonthIcon
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "#6c757d",
                }}
              />
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Billing Party</label>
            <Select
              name="billing_party"
              value={formData.billing_party ? { value: formData.billing_party, label: formData.billing_party } : null}
              onChange={(val) => setFormData({ ...formData, billing_party: val?.value || "" })}
              options={[
                { value: "Shipper", label: "Shipper" },
                { value: "Consignee", label: "Consignee" },
                { value: "Notify Party", label: "Notify Party" },
                { value: "Other Party", label: "Other Party" },
              ]}
              placeholder="Select Billing Party"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Shipper"
              name="shipper"
              value={formData.shipper}
              onChange={handleChange}
              placeholder="Enter Shipper"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Consignee"
              name="consignee"
              value={formData.consignee}
              onChange={handleChange}
              placeholder="Enter Consignee"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Notify Party"
              name="notify_party"
              value={formData.notify_party}
              onChange={handleChange}
              placeholder="Enter Notify Party"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Port of Loading</label>
            <Select
              options={portsOptions}
              value={
                portsOptions.find((p) => p.value === formData.pol) ||
                (formData.pol ? { value: formData.pol, label: formData.pol } : null)
              }
              onChange={handlePolChange}
              placeholder="Select Port of Loading"
              isClearable
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Port of Discharge</label>
            <Select
              options={portsOptions}
              value={
                portsOptions.find((p) => p.value === formData.pod) ||
                (formData.pod ? { value: formData.pod, label: formData.pod } : null)
              }
              onChange={handlePodChange}
              placeholder="Select Port of Discharge"
              isClearable
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Place of Receipt"
              name="place_of_receipt"
              value={formData.place_of_receipt}
              onChange={handleChange}
              placeholder="Enter Place of Receipt"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Final Delivery"
              name="final_delivery"
              value={formData.final_delivery}
              onChange={handleChange}
              placeholder="Enter Final Delivery"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Shipment Segment"
              name="shipment_segment"
              value={formData.shipment_segment}
              onChange={handleChange}
              placeholder="Enter Shipment Segment"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Incoterms"
              name="incoterms"
              value={formData.incoterms}
              onChange={handleChange}
              placeholder="Enter Incoterms"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Commodity Name"
              name="commodity_name"
              value={formData.commodity_name}
              onChange={handleChange}
              placeholder="Enter Commodity Name"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Freight Terms"
              name="freight_terms"
              value={formData.freight_terms}
              onChange={handleChange}
              placeholder="Enter Freight Terms"
            />
          </div>
          {["FE", "FI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="Gross Weight"
                name="gross_weight"
                value={formData.gross_weight}
                onChange={handleChange}
                placeholder="Enter Gross Weight"
              />
            </div>
          )}
          {["LE", "LI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="CBM"
                name="cbm"
                value={formData.cbm}
                onChange={handleChange}
                placeholder="Enter CBM"
              />
            </div>
          )}
          {["AE", "AI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="Chargeable Weight"
                name="chargeable_weight"
                value={formData.chargeable_weight}
                onChange={handleChange}
                placeholder="Enter Chargeable Weight"
              />
            </div>
          )}
          {["FE", "FI", "LE", "LI"].includes(formData.shipment_segment) && containerTableData.length > 0 && (
            <div className="col-12 mb-3">
              <h5 className="fs-6 fw-bold">
                Container Details
              </h5>
              <table className="table table-bordered" style={{ marginTop: '10px' }}>
                <thead>
                  <tr>
                    {formData.shipment_segment !== "LE" && formData.shipment_segment !== "LI" && (
                      <th>Container Type</th>
                    )}
                    <th>Weight</th>
                    <th>Length</th>
                    <th>Height</th>
                    <th>Breadth</th>
                    {formData.nature_of_cargo === "Temperature Control" && (
                      <th>Temperature</th>
                    )}
                    <th>Container Number</th>
                  </tr>
                </thead>
                <tbody>
                  {containerTableData.map((container, index) => (
                    <tr key={index}>
                      {formData.shipment_segment !== "LE" && formData.shipment_segment !== "LI" && (
                        <td>{container.container_type}</td>
                      )}
                      <td>{container.container_weight}</td>
                      <td>{container.container_length}</td>
                      <td>{container.container_height}</td>
                      <td>{container.container_breadth}</td>
                      {formData.nature_of_cargo === "Temperature Control" && (
                        <td>{container.container_temperature}</td>
                      )}
                      <td>
                        <InputField
                          placeholder="Enter Container Number"
                          value={container.container_number || ""}
                          onChange={(e) => handleContainerChange(index, "container_number", e.target.value)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {["AE", "AI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label="Airline Name"
                name="airline_name"
                value={formData.airline_name}
                onChange={handleChange}
                placeholder="Enter Airline Name"
              />
            </div>
          )}
          {["LE", "LI"].includes(formData.shipment_segment) && (
            <div className="col-md-4">
              <InputField
                label={formData.shipment_segment === "LI" ? "Destuffing At" : "Stuffing At"}
                name="consolidation_at"
                value={formData.consolidation_at}
                onChange={handleChange}
                placeholder={`Enter ${formData.shipment_segment === "LI" ? "Destuffing At" : "Stuffing At"}`}
              />
            </div>
          )}
          <div className="col-md-4">
            <InputField
              label="Vessel Name"
              name="vessel_name"
              value={formData.vessel_name}
              onChange={handleChange}
              placeholder="Enter Vessel Name"
            />
          </div>
          <div className="col-md-4">
            <InputField
              label="Voyage Number"
              name="voyage_number"
              value={formData.voyage_number}
              onChange={handleChange}
              placeholder="Enter Voyage Number"
            />
          </div>
</div>

        <div className="d-flex " style={{ gap: "1.5rem" }}>
          <div
            className="mb-4 position-relative fullwidth"
            style={{ maxHeight: "100%" }}
          >
            <table className="table table-bordered tbllbl mb-2 mb-md-3">
              <thead className="table-light">
                <tr>
                  <th>Charge Code</th>
                  <th>SAC</th>
                  <th>Currency</th>
                  <th>Rate</th>
                  <th>Quantity</th>
                  <th>Amount</th>
                  <th>Amount in INR</th>
                  <th>GST</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                   <td>
                     <SelectField
                       options={chargeCodeOptions}
                       value={inputChargeRow.charge_code}
                       onChange={(val) => {
                         handleInputChargeChange("charge_code", val);
                         fetchGSTForChargeCode(val?.value, "input");
                       }}
                       isLoading={loadingChargeCodes}
                       searchable
                     />
                   </td>
                  <td>
                    <InputField
                      value={inputChargeRow.sac_code || ""}
                      placeholder="SAC Code"
                      onChange={(e) =>
                        handleInputChargeChange("sac_code", e.target.value)
                      }
                    />
                  </td>
<td className="minselect">
                   <Select
                     options={currencyOptions}
                     value={
                       currencyOptions.find(
                         (c) => c.value === inputChargeRow.currency,
                       ) || null
                     }
                     onChange={(selected) =>
                       handleCurrencyChange("input", selected)
                     }
                     isLoading={loadingCurrencies}
                     placeholder="Select"
                   />
                </td>
                  <td>
                    <InputField
                      type="number"
                      placeholder="Rate"
                      value={inputChargeRow.rate || ""}
                      onChange={(e) =>
                        handleInputChargeChange("rate", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <InputField
                      type="number"
                      placeholder="Quantity"
                      value={inputChargeRow.quantity || ""}
                      onChange={(e) =>
                        handleInputChargeChange("quantity", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      value={inputChargeRow.amount || 0}
                      readOnly
                    />
                  </td>
                  <td>
                    <InputField
                      type="text"
                      value={inputChargeRow.amount_in_inr || 0}
                      readOnly
                    />
                  </td>
<td className="minselect">
                  <Select
                    options={[
                      { label: "Yes", value: "yes" },
                      { label: "No", value: "no" },
                    ]}
                    value={inputChargeRow.gst_option || null}
                    onChange={(selectedOption) =>
                      handleInputChargeChange("gst_option", selectedOption)
                    }
                    placeholder="Select"
                  />
                </td>
                <td className="text-center">
                    <AddCircleOutlineIcon
                      style={{
                        cursor: "pointer",
                        color: "green",
                        fontSize: 28,
                      }}
                      onClick={addChargeRow}
                    />
                  </td>
                </tr>
 
{/* Map existing rows */}
                {chargeRow.map((charge, index) => (
                  <tr key={index}>
                    <td>{charge.charge_code}</td>
                    <td>{charge.sac_code}</td>
                    <td>{charge.currency}</td>
                    <td>{charge.rate}</td>
                    <td>{charge.quantity}</td>
                    <td>{charge.amount}</td>
                    <td>{charge.amount_in_inr}</td>
                    <td>{charge.gst_option?.value === "yes" ? "Yes" : "No"}</td>
                    <td className="text-center">
                      <DeleteOutlineIcon
                        style={{
                          cursor: "pointer",
                          color: "red",
                          fontSize: 28,
                        }}
                        onClick={() => deleteChargeRow(index)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="table-error text-danger mb-2">
              {errors.chargeTable && <span>{errors.chargeTable}</span>}
            </div>
          </div>
        </div>

        {/* Exchange Rate & Totals Row */}
        <div className="d-flex justify-content-between align-items-start mb-4" style={{ gap: "1.5rem" }}>
          {/* Totals Table */}
          <div style={{ flex: "0 0 400px" }}>
            <table
              className="table"
              style={{
                width: "100%",
              }}
            >
              <tbody>
                <tr>
                  <td style={{ textAlign: "left", fontWeight: "bold" }}>
                    Sub Total
                  </td>
                  <td style={{ textAlign: "right" }}>{totals.subTotal}</td>
                </tr>
                <tr style={{ borderTop: "2px solid #dee2e6" }}>
                  <td style={{ textAlign: "left", fontWeight: "bold" }}>
                    GST Total
                  </td>
                  <td style={{ textAlign: "right", fontWeight: "bold" }}>
                    {totals.gstAmount}
                  </td>
                </tr>
                <tr className="table-secondary">
                  <td
                    style={{
                      textAlign: "left",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Grand Total
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    {totals.grandTotal}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Exchange Rate Box */}
          <div style={{ flex: "0 0 225px" }}>
            <div
              className="border rounded p-3"
              style={{
                backgroundColor: "#f8f9fa",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
              }}
            >
              <div className="d-flex align-items-center mb-2">
                <Tooltip
                  title="To update the exchange rate, please go to the Exchange Rate Master and confirm with your supervisor."
                >
                  <InfoIcon
                    title=""
                    sx={{
                      fontSize: "1rem",
                      cursor: "default",
                      color: "#6c757d",
                      mr: "4px",
                    }}
                  />
                </Tooltip>
                <strong style={{ fontSize: "0.875rem", color: "#495057" }}>
                  Exchange Rate
                </strong>
              </div>
              <div style={{ fontSize: "0.875rem", color: "#666" }}>
                {inputChargeRow.currency && inputChargeRow.currency !== "INR" && (
                  <div className="mb-1">
                    <strong>1 {inputChargeRow.currency}</strong> ={" "}
                    <strong>{inputChargeRow.exchange_rate}</strong> INR
                    <small className="text-muted ms-1">(Auto-fetched)</small>
                  </div>
                )}
                {chargeRow.map((charge, idx) => {
                  if (!charge.currency || charge.currency === "INR") return null;
                  return (
                    <div key={idx} className="mb-1">
                      <strong>1 {charge.currency}</strong> ={" "}
                      <strong>{charge.exchange_rate}</strong> INR
                    </div>
                  );
                })}
                {(!inputChargeRow.currency || inputChargeRow.currency === "INR") &&
                  chargeRow.every((c) => !c.currency || c.currency === "INR") && (
                    <div className="text-muted">No foreign currency selected</div>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* Term & Conditions */}
        <div className="row mt-3">
          <div className="col-md-4">
            <SelectField
              label="Term & Conditions"
              options={[
                "LCL IMPORT - NON HAZ CARGO",
                "LCL IMPORT - HAZ CARGO",
                "LCL EXPORT - NON HAZ CARGO",
                "LCL EXPORT- HAZ CARGO",
                "FCL IMPORT - NON HAZ CARGO ( Dock Stuffing )",
                "FCL IMPORT - NON HAZ CARGO ( Factory Stuffing )",
                "FCL IMPORT - HAZ CARGO ( Dock Stuffing )",
                "FCL IMPORT - HAZ CARGO ( Factory Stuffing )",
                "FCL EXPORT- NON HAZ CARGO ( Dock Stuffing )",
                "FCL EXPORT- NON HAZ CARGO ( Factory Stuffing )",
                "FCL EXPORT- HAZ CARGO ( Dock Stuffing )",
                "FCL EXPORT- HAZ CARGO ( Factory Stuffing )",
                "FCL EXPORT- Russia / CIS Sector Via Iran",
              ]}
              value={formData.term_and_condition}
              onChange={(val) => handleFormChange("term_and_condition", val)}
              error={errors.term_and_condition}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-between my-3 gap-3 align-items-center">
          <Button
            variant="darkblue"
            btncls="px-4"
            type="button"
            startIcon={<SaveIcon />}
            children="Create Performa Invoice"
            disabled={loading || loadingSubmit}
            onClick={(e) => handleSubmit(e, "performa")}
          />
          <Button
            variant="darkblue"
            btncls="px-4"
            type="button"
            startIcon={<SaveIcon />}
            children="Create Final Invoice"
            disabled={loading || loadingSubmit}
            onClick={(e) => handleSubmit(e, "final")}
          />
          {/* Hidden submit button to capture Enter key submission (defaults to Final) */}
          <button type="submit" style={{ display: 'none' }} />
        </div>
      </form>

        {/* Confirmation Modal */}
        {showConfirm && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Confirm {invoiceType === "performa" ? "Performa" : "Final"} Invoice Creation
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowConfirm(false)}
                    disabled={loadingSubmit}
                  />
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to create this {invoiceType} invoice?</p>
                  <p className="mb-0">
                    <strong>SubTotal:</strong> {totals.subTotal} |
                    <strong> GST:</strong> {totals.gstAmount} |
                    <strong> Total:</strong> {totals.grandTotal}
                  </p>
                </div>
                <div className="modal-footer">
                  <Button
                    className="btn btn-secondary"
                    onClick={() => setShowConfirm(false)}
                    disabled={loadingSubmit}
                    text="No"
                  />
                  <Button
                    className="btn btn-primary"
                    onClick={submitInvoice}
                    disabled={loadingSubmit}
                    text={loadingSubmit ? "Creating..." : `Yes, Create ${invoiceType === "performa" ? "Performa" : "Final"} Invoice`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

       {/* Success Modal */}
       {showSuccess && (
         <div
           className="modal fade show"
           style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
           tabIndex="-1"
         >
           <div className="modal-dialog modal-dialog-centered">
             <div className="modal-content">
               <div className="modal-header">
                 <h5 className="modal-title text-success">Success</h5>
                 <button
                   type="button"
                   className="btn-close"
                   onClick={() => setShowSuccess(false)}
                 />
               </div>
                <div className="modal-body">
                  <p>{invoiceType === "performa" ? "Performa" : "Final"} invoice created successfully!</p>
                </div>
               <div className="modal-footer">
                 <Button
                   type="button"
                   className="btn btn-primary"
                   onClick={() => {
                     setShowSuccess(false);
                      navigate("/billingAndInvoice/invoicing");
                   }}
                   text="Ok"
                 />
               </div>
             </div>
           </div>
         </div>
       )}

       {/* Modal Backdrop */}
       {(showConfirm || showSuccess) && (
         <div className="modal-backdrop fade show"></div>
       )}
     </>
   );
 };
 
export default InvoicingForm;